/**
 * Shared TTS Audio Module
 *
 * This module provides a singleton AudioContext that can be primed during a user gesture
 * and reused across components. This is essential for iOS Safari which requires AudioContext
 * to be created/resumed synchronously within a user interaction handler.
 *
 * Usage:
 * 1. Call primeAudioContext() synchronously at the START of any click handler before async work
 * 2. Call playAudio(url) to play audio using the primed context
 * 3. Call stopSpeaking() to stop current audio
 */

import { ttsSettings, setTTSSpeaking } from '../state/settings.svelte.js';

// Singleton AudioContext - shared across all components
let audioContext = null;
// Reusable audio element for Safari fallback
let reusableAudio = null;
// Current audio source for stopping
let currentAudioSource = null;
// Fetch for audio that has not started playing yet
let currentAudioRequest = null;
// Invalidates async playback work when audio is stopped or replaced
let playbackGeneration = 0;

/**
 * Prime the audio context - MUST be called synchronously in a click handler.
 * This is the key to making audio work on iOS Safari.
 *
 * Call this at the START of any click handler before any async work.
 */
export function primeAudioContext() {
  if (!ttsSettings.enabled) return;

  try {
    // Create AudioContext on user gesture - this is the key for Safari
    if (!audioContext || audioContext.state === 'closed') {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        audioContext = new AudioContextClass();
        console.log('🔊 [Shared] Created AudioContext, state:', audioContext.state);
      }
    }

    // Resume if suspended - must happen during user gesture
    if (audioContext && audioContext.state === 'suspended') {
      // Don't await - just kick it off synchronously
      audioContext.resume().then(() => {
        console.log('🔊 [Shared] AudioContext resumed, state:', audioContext.state);
      }).catch(e => console.warn('🔊 [Shared] Resume failed:', e));
    }

    // Also create reusable Audio element
    if (!reusableAudio) {
      reusableAudio = new Audio();
      console.log('🔊 [Shared] Created reusable Audio element');
    }

    console.log('🔊 [Shared] Audio priming complete, audioContext:', !!audioContext, 'state:', audioContext?.state);
  } catch (err) {
    console.warn('🔊 [Shared] Failed to prime audio:', err);
  }
}

/**
 * Check if audio context is primed and ready
 */
export function isAudioPrimed() {
  return audioContext && audioContext.state !== 'closed';
}

/**
 * Stop any currently playing audio
 */
export function stopSpeaking() {
  playbackGeneration += 1;

  if (currentAudioRequest) {
    currentAudioRequest.abort();
    currentAudioRequest = null;
  }

  const audioSource = currentAudioSource;
  currentAudioSource = null;

  if (audioSource) {
    try {
      // Prevent a delayed `ended` event from clearing a newer source.
      audioSource.onended = null;
      audioSource.stop();
      audioSource.disconnect();
    } catch (e) {
      // Ignore - might already be stopped
    }
  }

  if (reusableAudio) {
    try {
      reusableAudio.onended = null;
      reusableAudio.onerror = null;
      reusableAudio.pause();
      reusableAudio.currentTime = 0;
      reusableAudio.removeAttribute('src');
      reusableAudio.load();
    } catch (e) {
      // Ignore - the element may not have loaded a source yet
    }
  }

  setTTSSpeaking(false);
}

/**
 * Play audio from a URL using the primed AudioContext.
 * Falls back to HTML5 Audio if AudioContext isn't available.
 *
 * @param {string} url - The audio URL to play (can be a blob URL)
 */
export async function playAudio(url) {
  console.log('🔊 [Shared] playAudio called with url:', url?.substring(0, 50) + '...');
  if (!ttsSettings.enabled) {
    console.log('🔊 [Shared] TTS disabled, returning early');
    return;
  }

  // Stop any currently playing audio
  stopSpeaking();
  const generation = playbackGeneration;
  setTTSSpeaking(true);

  let request = null;
  const isCurrentPlayback = () => (
    generation === playbackGeneration && ttsSettings.enabled
  );

  try {
    // Try Web Audio API first (more reliable in Safari)
    if (audioContext && audioContext.state !== 'closed') {
      console.log('🔊 [Shared] Using Web Audio API for playback');

      // Resume context if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      if (!isCurrentPlayback()) return;

      // Fetch audio data
      request = new AbortController();
      currentAudioRequest = request;
      const response = await fetch(url, { signal: request.signal });
      const arrayBuffer = await response.arrayBuffer();

      if (!isCurrentPlayback()) return;

      // Decode audio
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // A newer question may have started while this audio was decoding.
      if (!isCurrentPlayback()) return;

      // Create and play source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      source.onended = () => {
        if (currentAudioSource !== source || !isCurrentPlayback()) return;
        console.log('🔊 [Shared] Web Audio playback ended');
        currentAudioSource = null;
        try {
          source.disconnect();
        } catch (e) {
          // Ignore - some browsers disconnect an ended source automatically
        }
        setTTSSpeaking(false);
      };

      currentAudioSource = source;
      if (currentAudioRequest === request) {
        currentAudioRequest = null;
      }
      source.start(0);
      console.log('🔊 [Shared] Web Audio playing!');
      return;
    }

    // Fallback to HTML5 Audio
    console.log('🔊 [Shared] Falling back to HTML5 Audio');
    const audioElement = reusableAudio || (reusableAudio = new Audio());

    if (!isCurrentPlayback()) return;

    audioElement.src = url;
    audioElement.volume = 1;

    audioElement.onended = () => {
      if (!isCurrentPlayback()) return;
      console.log('🔊 [Shared] Audio playback ended');
      setTTSSpeaking(false);
    };

    audioElement.onerror = (e) => {
      if (!isCurrentPlayback()) return;
      console.error('🔊 [Shared] Audio error event:', e);
      setTTSSpeaking(false);
    };

    await audioElement.play();
    // stopSpeaking() already paused/reset a superseded request. Do not pause
    // here because this shared element may now be playing a newer question.
    if (!isCurrentPlayback()) return;
    console.log('🔊 [Shared] HTML5 Audio playing!');
  } catch (err) {
    if (err?.name === 'AbortError' || !isCurrentPlayback()) return;
    console.error('🔊 [Shared] Audio playback error:', err);
    setTTSSpeaking(false);
  } finally {
    if (currentAudioRequest === request) {
      currentAudioRequest = null;
    }
  }
}

/**
 * Close and cleanup the audio context.
 * Only call this when completely done with audio (e.g., app cleanup).
 */
export function closeAudioContext() {
  stopSpeaking();
  if (audioContext) {
    audioContext.close();
    audioContext = null;
  }
  reusableAudio = null;
  console.log('🔊 [Shared] Audio context closed');
}
